import FormSectionSubtitle from "@rentiohq/shared-frontend/dist/components/components/FormSectionSubtitle";
import ListItem from "@rentiohq/shared-frontend/dist/components/components/ListItem";
import ListItemSeparator from "@rentiohq/shared-frontend/dist/components/components/ListItemSeparator";
import Spacer, {
  ESpacerDirection,
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { EPaymentMethod } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { IPaymentRequest } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { includesAny } from "@rentiohq/shared-frontend/dist/utils/array.utils";
import { max } from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  getPeriodForPaymentRequestItem,
  getSepaDueDate,
  getSepaLatestApprovalDate,
} from "@rentiohq/shared-frontend/dist/utils/payment.utils";
import * as paymentOrderUtils from "@rentiohq/shared-frontend/dist/utils/paymentOrder.utils";
import { join } from "@rentiohq/shared-frontend/dist/utils/string.utils";
import uniq from "lodash/uniq";
import React from "react";
import images from "../../../../assets";
import * as SPaymentMethods from "../../routes/PaymentMethodSelect/PaymentMethodSelect.styles";
import * as S from "./Receipt.styles";

const NOW = new Date();

interface IProps {
  paymentRequest: IPaymentRequest;
  property?: IProperty;
  sepaBankDaysOffset?: number;
}

class Receipt extends React.Component<IProps> {
  // Render
  public render() {
    const { paymentRequest, property, sepaBankDaysOffset } = this.props;
    const { paymentRequestItems, createdAt } = paymentRequest;

    const sepaApprovalDate = formatDate(
      getSepaLatestApprovalDate(paymentRequest, sepaBankDaysOffset),
    );
    const sepaDueDate = formatDate(
      getSepaDueDate(paymentRequest, sepaBankDaysOffset),
    );
    const dueDate = formatDate(max([NOW, paymentRequest.dueDateAt]));
    const paymentMethods = join(
      paymentRequest.allowedPaymentMethods
        ?.map(x => getLocalizedText(`payment_request.method.${x}`))
        .sort(),
      ", ",
      ` ${getLocalizedText("system.or").toLowerCase()} `,
    );

    const propertyAddress = property
      ? formatAddress(property)
      : paymentRequest?.propertyAddress?.full ||
        getLocalizedText("payment.receipt.no_property");

    return (
      <S.Wrap>
        <S.InnerWrap>
          <S.HeaderWrap>
            <S.HeaderLogo src={images.IMG_RENTIO_LOGO} />
            <S.HeaderTitle>{formatDate(createdAt)}</S.HeaderTitle>
          </S.HeaderWrap>

          <Spacer weight={ESpacerWeight.W32} />

          <FormSectionSubtitle title={propertyAddress} />

          {paymentRequestItems.map(paymentRequestItem => {
            const { id, paymentOrder, amount } = paymentRequestItem;
            const period = getPeriodForPaymentRequestItem(paymentRequestItem);

            return (
              <React.Fragment key={id}>
                <ListItem
                  title={paymentOrderUtils.getTitle(paymentOrder)}
                  subtitle={period}
                  disclosureInfo={
                    <S.Value>{formatCurrency(amount, true)}</S.Value>
                  }
                />
                <S.ListItemSeparatorWrap>
                  <ListItemSeparator />
                </S.ListItemSeparatorWrap>
              </React.Fragment>
            );
          })}

          <ListItem
            title={
              <S.TotalTitle>
                {getLocalizedText("payment.receipt.total")}
              </S.TotalTitle>
            }
            disclosureInfo={
              <S.TotalValue>
                {formatCurrency(paymentRequest.originalAmount, true)}
              </S.TotalValue>
            }
          />

          <Spacer weight={ESpacerWeight.W32} />

          <S.PayDateInfoWrap>
            {paymentRequestItems.length > 0 && (
              <>
                <S.PayBeforeDate>
                  {getLocalizedText("payment.receipt.pay_before.unified", {
                    sepaApprovalDate,
                    sepaDueDate,
                    dueDate,
                    paymentMethods,
                  })}
                </S.PayBeforeDate>

                {includesAny(paymentRequest.allowedPaymentMethods, [
                  EPaymentMethod.Sepa,
                  EPaymentMethod.SepaOnce,
                  EPaymentMethod.SepaRecurring,
                ]) && (
                  <>
                    <Spacer weight={ESpacerWeight.W04} />
                    <S.PayCompletedDate>
                      {getLocalizedText(
                        "payment.receipt.pay_completed.unified",
                        {
                          sepaApprovalDate,
                          sepaDueDate,
                          dueDate,
                          paymentMethods,
                        },
                      )}
                    </S.PayCompletedDate>
                  </>
                )}

                {this.renderPaymentMethodIcons(paymentRequest)}
              </>
            )}
          </S.PayDateInfoWrap>
        </S.InnerWrap>
      </S.Wrap>
    );
  }

  private renderPaymentMethodIcons = (paymentRequest: IPaymentRequest) => {
    const { allowedPaymentMethods } = paymentRequest;

    const paymentMethodsMapped = uniq(
      allowedPaymentMethods.map(paymentMethod => {
        if (
          paymentMethod === EPaymentMethod.SepaOnce ||
          paymentMethod === EPaymentMethod.SepaRecurring
        ) {
          return EPaymentMethod.Sepa;
        }
        return paymentMethod;
      }),
    );

    return (
      <S.PaymentMethodIconWrap>
        {paymentMethodsMapped.map((paymentMethod, index) => (
          <>
            {index > 0 && (
              <Spacer
                weight={ESpacerWeight.W04}
                direction={ESpacerDirection.Horizontal}
              />
            )}
            {this.renderPaymentMethodIcon(paymentMethod)}
          </>
        ))}
      </S.PaymentMethodIconWrap>
    );
  };

  private renderPaymentMethodIcon = (paymentMethod: EPaymentMethod) => {
    switch (paymentMethod) {
      case EPaymentMethod.Sepa:
      case EPaymentMethod.SepaOnce:
      case EPaymentMethod.SepaRecurring:
        return <SPaymentMethods.PaymentMethodIconSepa />;

      // case EPaymentMethod.Ibanisation:
      //   return <SPaymentMethods.PaymentMethodIconIbanisation />;

      case EPaymentMethod.Bancontact:
        return <SPaymentMethods.PaymentMethodIconBcmc />;

      case EPaymentMethod.Payconiq:
        return <SPaymentMethods.PaymentMethodIconPayconiq />;

      case EPaymentMethod.Ideal:
        return <SPaymentMethods.PaymentMethodIconIdealWero />;

      default:
        break;
    }
  };
}

// eslint-disable-next-line import/no-default-export
export default Receipt;
